import React, { useState, useEffect } from "react";
import { db, storage } from "../../lib/firebase";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Spinner } from "../../components/common/Spinner";
import { Button } from "../../components/common/Button";
import { Card } from "../../components/common/Card";
import { toast } from "react-hot-toast";
import { adminWalletOp } from "../../services/callables";

type KitchenSectionProps = {
  onAdminEvent?: () => void;
  trigger?: number;
};

// --- 1. Chef Quick Create Component ---
type ChefQuickCreateProps = {
  onChefCreated: (chef: {
    uid: string;
    name: string;
    phone: string;
    roles: string[];
  }) => void;
};

const ChefQuickCreate: React.FC<ChefQuickCreateProps> = ({ onChefCreated }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [roles, setRoles] = useState<string[]>(["chef"]);
  const [loading, setLoading] = useState(false);

  function handleRoleChange(role: string) {
    setRoles((r) =>
      r.includes(role) ? r.filter((x) => x !== role) : [...r, role]
    );
  }

  async function handleCreateChef(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const docRef = doc(collection(db, "users"));
      await setDoc(docRef, {
        uid: docRef.id,
        name,
        phone,
        roles,
        onboardingComplete: false,
        createdAt: new Date(),
        walletBalance: 0,
      });

      // Wallet docs are server-managed (rules block client writes)
      await adminWalletOp({ op: "ensure", walletId: docRef.id, ownerType: "user" });

      setName("");
      setPhone("");
      setRoles(["chef"]);
      onChefCreated({ uid: docRef.id, name, phone, roles });
      toast.success("Chef created successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create chef.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form
      onSubmit={handleCreateChef}
      className="flex flex-wrap gap-2 my-2 items-end bg-gray-50 p-3 rounded border border-gray-200"
    >
      <div>
        <label className="text-xs font-bold text-gray-600 block mb-1">
          Chef Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="form-control py-1"
          required
        />
      </div>
      <div>
        <label className="text-xs font-bold text-gray-600 block mb-1">
          Phone
        </label>
        <input
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="form-control py-1"
          required
        />
      </div>

      <div className="flex flex-col justify-end">
        <label className="text-xs font-bold text-gray-600 block mb-1">
          Roles
        </label>
        <div className="flex gap-2">
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={roles.includes("chef")}
              onChange={() => handleRoleChange("chef")}
            />{" "}
            Chef
          </label>
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={roles.includes("delivery")}
              onChange={() => handleRoleChange("delivery")}
            />{" "}
            Delivery
          </label>
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={roles.includes("admin")}
              onChange={() => handleRoleChange("admin")}
            />{" "}
            Admin
          </label>
        </div>
      </div>

      <div className="ml-auto">
        <Button type="submit" isLoading={loading} size="sm" className="h-9">
          + Create
        </Button>
      </div>
    </form>
  );
};

// ✅ Assign Chefs Modal Component
type AssignChefsModalProps = {
  kitchen: any;
  allChefs: any[];
  onClose: () => void;
  onSave: (kitchenId: string, chefIds: string[]) => void;
};

const AssignChefsModal: React.FC<AssignChefsModalProps> = ({
  kitchen,
  allChefs,
  onClose,
  onSave,
}) => {
  const [selectedChefs, setSelectedChefs] = useState<string[]>(
    kitchen.chefUids || (kitchen.chefUid ? [kitchen.chefUid] : [])
  );
  const [saving, setSaving] = useState(false);

  const handleToggleChef = (chefId: string) => {
    setSelectedChefs((prev) =>
      prev.includes(chefId)
        ? prev.filter((id) => id !== chefId)
        : [...prev, chefId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(kitchen.id, selectedChefs);
      toast.success("Chefs assigned successfully!");
      onClose();
    } catch (error) {
      toast.error("Failed to assign chefs");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-lg font-bold text-gray-900">Assign Chefs</h3>
          <p className="text-sm text-gray-600 mt-1">{kitchen.name}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {allChefs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No chefs available</p>
              <p className="text-sm mt-2">Create a chef first to assign</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allChefs.map((chef) => (
                <label
                  key={chef.uid}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedChefs.includes(chef.uid)
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedChefs.includes(chef.uid)}
                    onChange={() => handleToggleChef(chef.uid)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {chef.name}
                    </div>
                    <div className="text-sm text-gray-600">{chef.phone}</div>
                  </div>
                  {selectedChefs.includes(chef.uid) && (
                    <span className="text-blue-600 text-sm font-semibold">
                      ✓
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <Button
            onClick={handleSave}
            variant="primary"
            isLoading={saving}
            className="flex-1"
            disabled={selectedChefs.length === 0}
          >
            Assign {selectedChefs.length > 0 && `(${selectedChefs.length})`}
          </Button>
          <Button
            onClick={onClose}
            variant="secondary"
            className="flex-1"
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

// ✅ Edit Kitchen Modal (details, location, logo, FSSAI)
type EditKitchenModalProps = {
  kitchen: any;
  onClose: () => void;
  onSaved: () => void;
};

const EditKitchenModal: React.FC<EditKitchenModalProps> = ({
  kitchen,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState(kitchen.name || kitchen.kitchen_name || "");
  const [description, setDescription] = useState(kitchen.description || "");
  const [city, setCity] = useState(kitchen.city || "");
  const [phone, setPhone] = useState(kitchen.phone || "");
  const [latitude, setLatitude] = useState<string>(
    kitchen.latitude != null ? String(kitchen.latitude) : ""
  );
  const [longitude, setLongitude] = useState<string>(
    kitchen.longitude != null ? String(kitchen.longitude) : ""
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [fssaiFiles, setFssaiFiles] = useState<FileList | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updates: any = {
        name: name.trim(),
        kitchen_name: name.trim(),
        description: description.trim(),
        city: city.trim(),
        phone: phone.trim(),
        updatedAt: new Date(),
      };

      if (latitude && longitude) {
        updates.latitude = Number(latitude);
        updates.longitude = Number(longitude);
      }

      if (logoFile) {
        const logoRef = ref(
          storage,
          `kitchen_logos/${kitchen.id}_logo_${Date.now()}`
        );
        await uploadBytes(logoRef, logoFile);
        const logoUrl = await getDownloadURL(logoRef);
        updates.logoUrl = logoUrl;
      }

      if (fssaiFiles && fssaiFiles.length > 0) {
        const existing: string[] = kitchen.fssaiFileUrls || [];
        const newUrls: string[] = [];

        for (let i = 0; i < fssaiFiles.length; i++) {
          const file = fssaiFiles[i];
          const fRef = ref(
            storage,
            `kitchen_fssai/${kitchen.id}_${file.name}_${Date.now()}`
          );
          await uploadBytes(fRef, file);
          const url = await getDownloadURL(fRef);
          newUrls.push(url);
        }

        updates.fssaiFileUrls = [...existing, ...newUrls];
      }

      await updateDoc(doc(db, "kitchens", kitchen.id), updates);

      toast.success("Kitchen updated");
      onSaved();
      onClose();
    } catch (error) {
      console.error("Kitchen update error:", error);
      toast.error("Failed to update kitchen");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full shadow-xl">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Edit Kitchen</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Kitchen Name
            </label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Description
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                City / Area
              </label>
              <input
                type="text"
                className="form-control"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                className="form-control"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="number"
                step="0.000001"
                className="form-control"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="number"
                step="0.000001"
                className="form-control"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Logo
            </label>
            <input
              type="file"
              accept="image/*"
              className="text-xs text-gray-600"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setLogoFile(file);
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              FSSAI Documents (PDF / Images)
            </label>
            <input
              type="file"
              multiple
              accept="image/*,.pdf"
              className="text-xs text-gray-600"
              onChange={(e) => setFssaiFiles(e.target.files)}
            />
            {kitchen.fssaiFileUrls?.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Existing files: {kitchen.fssaiFileUrls.length}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t mt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={isSaving}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- 2. Kitchen Onboarding Component ---
type KitchenOnboardingProps = {
  onAdminEvent: () => void;
};

const KitchenOnboarding: React.FC<KitchenOnboardingProps> = ({
  onAdminEvent,
}) => {
  const [name, setName] = useState("");
  const [chefUid, setChefUid] = useState("");
  const [chefs, setChefs] = useState<any[]>([]);
  const [desc, setDesc] = useState("");
  const [phone, setPhone] = useState("");
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showChef, setShowChef] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function fetchChefs() {
    const snap = await getDocs(collection(db, "users"));
    setChefs(
      snap.docs
        .filter((d) => d.data().roles?.includes("chef"))
        .map((d) => ({ ...d.data(), uid: d.id }))
    );
  }

  useEffect(() => {
    fetchChefs();
  }, []);

  async function handleOnboardKitchen(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let logoUrl = "";
      if (logo) {
        const storageRef = ref(
          storage,
          `kitchen_logos/${name.replace(/\s+/g, "_")}_${Date.now()}`
        );
        await uploadBytes(storageRef, logo);
        logoUrl = await getDownloadURL(storageRef);
      }

      const kitchenId = name.replace(/\s+/g, "_").toLowerCase();

      await setDoc(doc(db, "kitchens", kitchenId), {
        id: kitchenId,
        name,
        kitchen_name: name,
        chefUid,
        ownerUid: chefUid,
        chefUids: [chefUid],
        phone,
        description: desc,
        logoUrl,
        status: "active",
        subscriptionStatus: "active",
        createdAt: new Date(),
      });

      setName("");
      setChefUid("");
      setPhone("");
      setDesc("");
      setLogo(null);
      setLogoPreview(null);
      onAdminEvent();
      toast.success("Kitchen onboarded successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to onboard kitchen.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card className="mb-8 p-5 border-l-4 border-orange-500">
      <h3 className="text-lg font-bold mb-4 text-gray-800">
        Onboard New Kitchen
      </h3>
      <form
        onSubmit={handleOnboardKitchen}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label text-xs">Kitchen Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-control"
              required
            />
          </div>
          <div>
            <label className="form-label text-xs">Contact Phone</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="form-control"
              required
            />
          </div>
        </div>

        <div>
          <label className="form-label text-xs">Assign Main Chef</label>
          <div className="flex gap-2">
            <select
              value={chefUid}
              onChange={(e) => setChefUid(e.target.value)}
              className="form-control flex-1"
              required
            >
              <option value="">Select a Chef...</option>
              {chefs.map((c) => (
                <option value={c.uid} key={c.uid}>
                  {c.name} ({c.phone})
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowChef((v) => !v)}
              className="whitespace-nowrap"
            >
              {showChef ? "Close" : "+ New Chef"}
            </Button>
          </div>
          {showChef && (
            <div className="mt-2 animate-in slide-in-from-top-2">
              <ChefQuickCreate
                onChefCreated={(chef) => {
                  fetchChefs();
                  setShowChef(false);
                  setChefUid(chef.uid);
                }}
              />
            </div>
          )}
        </div>

        <div>
          <label className="form-label text-xs">Description</label>
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            className="form-control"
            rows={2}
          />
        </div>

        <div>
          <label className="form-label text-xs">Logo</label>
          <div className="flex items-center gap-4">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Preview"
                className="w-16 h-16 rounded-full object-cover border border-gray-200"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs border border-dashed border-gray-300">
                No Logo
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="text-xs text-gray-500"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setLogo(f);
                  setLogoPreview(URL.createObjectURL(f));
                }
              }}
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            className="px-6"
          >
            Onboard Kitchen
          </Button>
        </div>
      </form>
    </Card>
  );
};

// ✅ Enhanced Kitchen List with Edit / Assign Chefs / Status buttons
type EnhancedKitchenListProps = {
  kitchens: any[];
  allChefs: any[];
  onAssignChefs: (kitchen: any) => void;
  onEditKitchen: (kitchen: any) => void;
  onStatusChange: (
    kitchenId: string,
    status: "active" | "paused" | "blocked"
  ) => void;
};

const EnhancedKitchenList: React.FC<EnhancedKitchenListProps> = ({
  kitchens,
  allChefs,
  onAssignChefs,
  onEditKitchen,
  onStatusChange,
}) => {
  if (kitchens.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-500">No kitchens onboarded yet</p>
      </Card>
    );
  }

  const isOpenLike = (status?: string) =>
    status === "active" || status === "open";

  const statusBadgeClass = (status?: string) => {
    if (isOpenLike(status)) return "bg-green-100 text-green-800";
    if (status === "paused") return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  const renderActions = (kitchen: any, align: "start" | "end" = "start") => (
    <div
      className={`flex items-center gap-2 flex-wrap ${
        align === "end" ? "justify-end" : ""
      }`}
    >
      <Button
        onClick={() => onAssignChefs(kitchen)}
        variant="secondary"
        size="sm"
      >
        👥 Chefs
      </Button>
      <Button
        onClick={() => onEditKitchen(kitchen)}
        variant="secondary"
        size="sm"
      >
        ✏ Edit
      </Button>
      <Button
        size="sm"
        variant={isOpenLike(kitchen.status) ? "primary" : "secondary"}
        onClick={() => onStatusChange(kitchen.id, "active")}
      >
        Open
      </Button>
      <Button
        size="sm"
        variant={kitchen.status === "paused" ? "primary" : "secondary"}
        onClick={() => onStatusChange(kitchen.id, "paused")}
      >
        Pause
      </Button>
      <Button
        size="sm"
        variant={kitchen.status === "blocked" ? "primary" : "secondary"}
        onClick={() => onStatusChange(kitchen.id, "blocked")}
      >
        Block
      </Button>
    </div>
  );

  const renderKitchenMeta = (kitchen: any) => {
    const assignedChefs = allChefs.filter(
      (chef) =>
        kitchen.chefUids?.includes(chef.uid) ||
        kitchen.chefUid === chef.uid
    );

    return {
      assignedChefs,
      logo: kitchen.logoUrl ? (
        <img
          src={kitchen.logoUrl}
          alt=""
          className="w-10 h-10 rounded-full object-cover"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold">
          {kitchen.name?.[0] || "K"}
        </div>
      ),
      chefsNode:
        assignedChefs.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {assignedChefs.map((chef) => (
              <span
                key={chef.uid}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {chef.name}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-gray-400">No chefs assigned</span>
        ),
    };
  };

  return (
    <>
      {/* Desktop table */}
      <Card className="overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kitchen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned Chefs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {kitchens.map((kitchen) => {
                const { logo, chefsNode } = renderKitchenMeta(kitchen);
                return (
                  <tr key={kitchen.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {logo}
                        <div>
                          <div className="font-medium text-gray-900">
                            {kitchen.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {kitchen.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {kitchen.phone}
                    </td>
                    <td className="px-6 py-4">{chefsNode}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(
                          kitchen.status
                        )}`}
                      >
                        {kitchen.status || "active"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {renderActions(kitchen, "end")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile card stack (~390px) */}
      <div className="md:hidden space-y-3">
        {kitchens.map((kitchen) => {
          const { logo, chefsNode } = renderKitchenMeta(kitchen);
          return (
            <Card key={kitchen.id} className="p-4 space-y-3">
              <div className="flex items-start gap-3">
                {logo}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {kitchen.name}
                      </div>
                      {kitchen.description && (
                        <div className="text-sm text-gray-500 line-clamp-2">
                          {kitchen.description}
                        </div>
                      )}
                    </div>
                    <span
                      className={`shrink-0 inline-flex px-2 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(
                        kitchen.status
                      )}`}
                    >
                      {kitchen.status || "active"}
                    </span>
                  </div>
                  {kitchen.phone && (
                    <div className="text-sm text-gray-600 mt-1">
                      {kitchen.phone}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  Assigned Chefs
                </div>
                {chefsNode}
              </div>
              <div className="pt-1 border-t border-gray-100">
                {renderActions(kitchen)}
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
};

// --- 3. Main Exported Component ---
export const KitchenSection: React.FC<KitchenSectionProps> = ({
  onAdminEvent,
  trigger,
}) => {
  const [kitchens, setKitchens] = useState<any[]>([]);
  const [allChefs, setAllChefs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigningKitchen, setAssigningKitchen] = useState<any | null>(null);
  const [editingKitchen, setEditingKitchen] = useState<any | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const kitchensSnap = await getDocs(collection(db, "kitchens"));
      setKitchens(
        kitchensSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );

      const usersSnap = await getDocs(collection(db, "users"));
      const chefs = usersSnap.docs
        .filter((d) => d.data().roles?.includes("chef"))
        .map((d) => ({ uid: d.id, ...d.data() }));
      setAllChefs(chefs);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [trigger]);

  const handleRefresh = () => {
    loadData();
    onAdminEvent?.();
  };

  const handleStatusChange = async (
    kitchenId: string,
    status: "active" | "paused" | "blocked"
  ) => {
    try {
      await updateDoc(doc(db, "kitchens", kitchenId), {
        status,
        updatedAt: new Date(),
      });
      toast.success(`Kitchen ${status}`);
      handleRefresh();
    } catch (error) {
      console.error("Status update error:", error);
      toast.error("Failed to update status");
    }
  };

  const handleAssignChefsSave = async (
    kitchenId: string,
    chefIds: string[]
  ) => {
    await updateDoc(doc(db, "kitchens", kitchenId), {
      chefUids: chefIds,
      chefUid: chefIds[0] || null,
      updatedAt: new Date(),
    });
    handleRefresh();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            Kitchen Administration
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {kitchens.length} kitchens, {allChefs.length} chefs
          </p>
        </div>
      </div>

      <KitchenOnboarding onAdminEvent={handleRefresh} />

      <EnhancedKitchenList
        kitchens={kitchens}
        allChefs={allChefs}
        onAssignChefs={(k) => setAssigningKitchen(k)}
        onEditKitchen={(k) => setEditingKitchen(k)}
        onStatusChange={handleStatusChange}
      />

      {assigningKitchen && (
        <AssignChefsModal
          kitchen={assigningKitchen}
          allChefs={allChefs}
          onClose={() => setAssigningKitchen(null)}
          onSave={handleAssignChefsSave}
        />
      )}

      {editingKitchen && (
        <EditKitchenModal
          kitchen={editingKitchen}
          onClose={() => setEditingKitchen(null)}
          onSaved={handleRefresh}
        />
      )}
    </div>
  );
};

export default KitchenSection;
